import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";

// Symbol mark: three teal bars — inline SVG so we can control size/color.
function Mark({ color = "#4FBEBE", size = 22, style }) {
  return (
    <svg
      viewBox="0 0 672 727"
      width={size}
      height={(size * 727) / 672}
      style={style}
      aria-hidden="true"
    >
      <g fill={color}>
        <rect x="36" y="36" width="600" height="135" rx="29.7" />
        <rect x="126" y="296" width="420" height="135" rx="29.7" />
        <rect x="36" y="556" width="600" height="135" rx="29.7" />
      </g>
    </svg>
  );
}

// Wordmark: SOMVEIL text where the "E" is rendered as three teal bars.
function Wordmark({ color = "#FAF7F2", accent = "#4FBEBE", size = 24 }) {
  return (
    <span
      style={{
        fontFamily: "'Jost', sans-serif",
        fontWeight: 500,
        fontSize: size,
        letterSpacing: "0.2em",
        color,
        display: "inline-flex",
        alignItems: "center",
        lineHeight: 1,
      }}
    >
      SOMV
      <span
        aria-hidden="true"
        style={{
          display: "inline-flex",
          flexDirection: "column",
          justifyContent: "center",
          gap: size * 0.13,
          marginLeft: size * 0.06,
          marginRight: size * 0.28,
          height: size * 0.72,
        }}
      >
        <span style={{ background: accent, height: size * 0.12, width: size * 0.55, borderRadius: 2 }} />
        <span style={{ background: accent, height: size * 0.12, width: size * 0.4, borderRadius: 2 }} />
        <span style={{ background: accent, height: size * 0.12, width: size * 0.55, borderRadius: 2 }} />
      </span>
      <span style={{ marginLeft: `-${size * 0.15}px` }}>IL</span>
      <span className="sr-only">Somveil</span>
    </span>
  );
}

const STATS = [
  {
    idx: "01 / 03",
    big: "104 million",
    accentIdx: 0,
    desc: "Indians of working age suffer from OSA — more than the entire population of Germany.",
    quote: "\u201CMore Indians have sleep apnea than Germany has people.\u201D",
    src: "AIIMS Systematic Review · Sleep Medicine Reviews · 2023",
  },
  {
    idx: "02 / 03",
    big: "30%",
    accentIdx: 1,
    desc: "reduction in cognitive performance — memory, focus, decision-making — from untreated OSA.",
    quote: "\u201C30% of your thinking capacity. Lost. Every single day.\u201D",
    src: "Industry health analysis · EIN Presswire · September 2025",
  },
  {
    idx: "03 / 03",
    big: "9 in 10",
    accentIdx: 2,
    desc: "women with sleep apnea in India go undiagnosed — the most overlooked group in the country.",
    quote: "\u201CIt's not a men's problem. Women are just the ones no one looks for.\u201D",
    src: "AIIMS Systematic Review · 2023 · global female under-diagnosis data",
  },
];

const STEPS = [
  { n: "01", t: "Take the 2-minute quiz", d: "Answer a few gentle questions about your sleep, snoring and energy. Nothing invasive, and no sign-up." },
  { n: "02", t: "See your risk, clearly", d: "Get a private, easy-to-read assessment of your OSA risk — and what it actually means for you, in plain language." },
  { n: "03", t: "Meet a verified specialist", d: "If it helps, we match you with credential-checked sleep doctors near you. You decide who to see, and when." },
];

const TESTI = [
  { q: "I'd blamed my exhaustion on work for years. Two minutes on Somveil finally pointed me to someone who understood.", i: "RM", n: "Rohan M.", c: "Bengaluru" },
  { q: "My husband's snoring frightened me at night. Somveil made the next step feel simple, not scary.", i: "PS", n: "Priya S.", c: "Pune" },
  { q: "Calm, clear, and no pressure at all. For the first time, I knew exactly what to do next.", i: "AK", n: "Arjun K.", c: "New Delhi" },
];

const FAQ = [
  { q: "Is the quiz really free?", a: "Yes. The 2-minute risk quiz is completely free and needs no sign-up. You only ever share details if you choose to connect with a specialist." },
  { q: "Is Somveil a diagnosis?", a: "No. Somveil helps you understand your risk and find the right specialist. A diagnosis comes from an accredited sleep doctor, often with a sleep study." },
  { q: "How are specialists verified?", a: "We check medical credentials, accreditations and patient reviews before any doctor appears on Somveil, and we re-verify them regularly." },
  { q: "Is my information private?", a: "Your quiz answers are private by default. We never sell your data, and you stay in control of exactly what is shared, and with whom." },
  { q: "What is obstructive sleep apnea?", a: "OSA is when your airway repeatedly narrows or closes during sleep, briefly interrupting your breathing and rest — often signalled by loud snoring, gasping, and daytime fatigue." },
];

const CITIES = ["Mumbai", "New Delhi", "Bengaluru", "Hyderabad", "Chennai", "Pune", "Kolkata"];

// Hero quiz card demo — three sample questions.
const DEMO_Q = [
  { q: "How often do you snore loudly?", o: ["Rarely or never", "A few nights a week", "Almost every night", "I\u2019m not sure"] },
  { q: "Do you ever wake up gasping or choking?", o: ["Never", "Once in a while", "More than I\u2019d like"] },
  { q: "How do you feel most mornings?", o: ["Refreshed", "A little groggy", "Exhausted, most days"] },
];

function HeroQuizCard() {
  const [q, setQ] = useState(0);
  const [picks, setPicks] = useState([]);
  const [done, setDone] = useState(false);
  const total = 8;
  const answered = picks.filter((x) => x != null).length;
  const progress = done ? 100 : Math.round((Math.min(answered, total) / total) * 100);

  const pick = (i) => {
    const next = picks.slice();
    next[q] = i;
    setPicks(next);
    setTimeout(() => {
      if (q >= DEMO_Q.length - 1) setDone(true);
      else setQ(q + 1);
    }, 380);
  };

  const restart = () => {
    setQ(0); setPicks([]); setDone(false);
  };

  const cur = DEMO_Q[Math.min(q, DEMO_Q.length - 1)];

  return (
    <div className="hn-hero-card-wrap" data-testid="hero-quiz-card">
      <div className="hn-hero-card-glow" aria-hidden="true">
        <span style={{ width: "72%" }} />
        <span style={{ width: "48%" }} />
        <span style={{ width: "64%" }} />
      </div>
      <div className="hn-hero-card">
        <div className="hn-hero-card-head">
          <Mark color="#0F6E6E" size={21} />
          <span className="hn-hero-card-eyebrow">Sleep Risk Quiz</span>
          <span className="hn-hero-card-free">FREE</span>
        </div>
        <div className="hn-hero-card-prog">
          <div style={{ width: `${progress}%` }} />
        </div>

        {!done && (
          <>
            <div className="hn-hero-card-meta">QUESTION {Math.min(q + 1, total)} OF {total}</div>
            <div className="hn-hero-card-q">{cur.q}</div>
            <div className="hn-hero-card-opts">
              {cur.o.map((label, i) => {
                const active = picks[q] === i;
                return (
                  <button
                    key={label}
                    onClick={() => pick(i)}
                    className={`hn-opt ${active ? "active" : ""}`}
                    data-testid={`hero-opt-${i}`}
                  >
                    <span className="dot">{active && <span />}</span>
                    <span>{label}</span>
                  </button>
                );
              })}
            </div>
          </>
        )}

        {done && (
          <div className="hn-hero-card-done">
            <div className="ring"><Mark color="#0F6E6E" size={26} /></div>
            <div className="ttl">That&apos;s the idea.</div>
            <p>Your real risk read takes all {total} questions — about two quiet minutes.</p>
            <Link to="/quiz" className="hn-btn-teal" data-testid="hero-card-full-quiz-cta">
              Take the full quiz <span>&rarr;</span>
            </Link>
            <div><button onClick={restart} className="hn-restart" data-testid="hero-card-restart">restart demo</button></div>
          </div>
        )}

        <div className="hn-hero-card-foot">
          <span>2 MIN</span><span>·</span><span>PRIVATE</span><span>·</span><span>NO SIGN-UP</span>
        </div>
      </div>
    </div>
  );
}

function FaqRow({ q, a, idx }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="hn-faq-row" data-testid={`faq-row-${idx}`}>
      <button onClick={() => setOpen(!open)} className="hn-faq-btn" aria-expanded={open}>
        <span>{q}</span>
        <span className={`hn-faq-plus ${open ? "open" : ""}`}>
          <span /><span />
        </span>
      </button>
      <div className="hn-faq-a" style={{ maxHeight: open ? 400 : 0 }}>
        <p>{a}</p>
      </div>
    </div>
  );
}

export default function Home() {
  useEffect(() => {
    // Warm the public stats endpoint; consumed by other pages/trust strip later.
    api.get("/public/stats").catch(() => {});
  }, []);

  return (
    <div className="hn-root" data-testid="home-page">
      <Nav theme="dark" />

      {/* HERO */}
      <section className="hn-hero" data-testid="hero-section">
        <div aria-hidden="true" className="hn-hero-orb hn-hero-orb-1" />
        <div aria-hidden="true" className="hn-hero-orb hn-hero-orb-2" />
        <div className="hn-hero-inner">
          <div className="hn-hero-copy">
            <div className="hn-hero-eyebrow">
              <Mark color="#4FBEBE" size={20} />
              <span>India&apos;s first sleep apnea discovery platform</span>
            </div>
            <h1 className="hn-hero-h1" data-testid="hero-headline">
              Nearly 1 billion people have sleep apnea.<br />Most don&apos;t know it.
            </h1>
            <p className="hn-hero-sub">
              Exhausted after a full night in bed? Loud snoring and restless nights can signal obstructive sleep apnea. Take the free 2-minute risk quiz and connect with verified specialists near you.
            </p>
            <div className="hn-hero-ctas">
              <Link to="/quiz" className="hn-btn-teal" data-testid="hero-cta-primary">
                Take the free 2-minute quiz <span className="arr">&rarr;</span>
              </Link>
              <a href="#how" className="hn-btn-ghost" data-testid="hero-cta-secondary">See how it works</a>
            </div>
            <div className="hn-hero-meta">Free &nbsp;·&nbsp; Private &nbsp;·&nbsp; No sign-up &nbsp;·&nbsp; 2 minutes</div>
          </div>
          <HeroQuizCard />
        </div>
      </section>

      {/* TRUST STRIP — hidden for now
      <section className="hn-trust" data-testid="trust-strip">
        <div className="hn-trust-inner">
          <span className="hn-trust-label">Trusted across India</span>
          <div className="hn-trust-stats">
            <span><strong>40+</strong> <em>cities</em></span>
            <span className="div" />
            <span><strong>500+</strong> <em>verified specialists</em></span>
            <span className="div" />
            <span><strong>50,000+</strong> <em>quizzes taken</em></span>
          </div>
        </div>
      </section>
      */}

      {/* WHY IT MATTERS */}
      <section id="why" className="hn-why" data-testid="why-section">
        <div className="hn-inner">
          <div className="hn-eyebrow-teal">Why it matters</div>
          <h2 className="hn-h2">Most sleep apnea in India goes unseen.</h2>
          <p className="hn-lede">
            Obstructive sleep apnea quietly interrupts your breathing over and over through the night. Most people never know it&apos;s happening — they only feel the exhaustion, the fog, and the strain it leaves behind.
          </p>
          <div className="hn-why-grid">
            {STATS.map((s, i) => (
              <div className="hn-why-card" key={s.idx} data-testid={`why-card-${i}`}>
                <div className="hn-why-idx">{s.idx}</div>
                <div className="hn-why-big">
                  {i === 0 ? (
                    <><span className="accent">104</span><span className="dim"> million</span></>
                  ) : (
                    <span className="accent">{s.big}</span>
                  )}
                </div>
                <div className="hn-why-desc">{s.desc}</div>
                <div className="hn-why-div" />
                <div className="hn-why-quote">{s.quote}</div>
                <div className="hn-why-src">{s.src}</div>
              </div>
            ))}
          </div>
          <div className="hn-why-note">Prevalence figures shown are indicative — to be confirmed against cited clinical sources before publication.</div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="hn-how" data-testid="how-section">
        <div className="hn-inner">
          <div className="hn-eyebrow-dark">How it works</div>
          <h2 className="hn-h2 dark">Clarity in three quiet steps.</h2>
          <p className="hn-lede dark">No clinics to cold-call, no jargon to decode. Just a calmer way to understand what your nights are telling you.</p>
          <div className="hn-how-grid">
            {STEPS.map((s, i) => (
              <div className="hn-how-card" key={s.n} data-testid={`how-card-${i}`}>
                <div className="hn-how-head">
                  <span className="hn-how-num">{s.n}</span>
                  <Mark color="#0F6E6E" size={26} />
                </div>
                <h3>{s.t}</h3>
                <p>{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VERIFIED SPECIALISTS — hidden for now, will be activated later.
          Kept in source (commented out) so we can restore it without a rebuild.
      <section id="specialists" className="hn-spec" data-testid="specialists-section">
        <div className="hn-inner">
          <div className="hn-spec-header">
            <div>
              <div className="hn-eyebrow-dark">Verified specialists</div>
              <h2 className="hn-h2 dark">Real doctors.<br />Verified. Near you.</h2>
              <p className="hn-lede dark" style={{ marginBottom: 0 }}>
                Every specialist on Somveil is credential-checked and reviewed — so the hardest part of your next step is already done.
              </p>
            </div>
            <Link to="/doctors" className="hn-btn-outline-dark" data-testid="specialists-browse-cta">
              Browse all specialists <span>&rarr;</span>
            </Link>
          </div>

          <div className="hn-spec-grid">
            {[
              { name: "Dr. Ananya Rao", spec: "Pulmonology · Sleep Medicine", city: "Bengaluru", rating: 4.9, reviews: 120 },
              { name: "Dr. Vikram Nair", spec: "ENT · Sleep Surgery", city: "Mumbai", rating: 4.8, reviews: 96 },
              { name: "Dr. Meera Iyer", spec: "Neurology · Sleep Disorders", city: "New Delhi", rating: 4.9, reviews: 143 },
            ].map((d, i) => (
              <div className="hn-spec-card" key={d.name} data-testid={`spec-card-${i}`}>
                <div className="hn-spec-photo">
                  <span>PHOTO — SPECIALIST</span>
                </div>
                <div className="hn-spec-body">
                  <div className="hn-spec-name-row">
                    <h3>{d.name}</h3>
                    <span className="hn-spec-verified">&#10003; VERIFIED</span>
                  </div>
                  <div className="hn-spec-spec">{d.spec}</div>
                  <div className="hn-spec-foot">
                    <span>{d.city}</span>
                    <span className="hn-spec-rating">&#9733; {d.rating} <em>({d.reviews})</em></span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="hn-spec-cities">
            <span className="hn-eyebrow-mini">In your city:</span>
            {CITIES.map((c) => (
              <span className="hn-city-pill" key={c}>{c}</span>
            ))}
            <span className="hn-city-more">+33 more</span>
          </div>
        </div>
      </section>
      */}

      {/* TESTIMONIALS — Featured editorial layout on deep-night bg */}
      <section id="stories" className="hn-testi" data-testid="testimonials-section">
        <div aria-hidden="true" className="hn-testi-orb hn-testi-orb-1" />
        <div aria-hidden="true" className="hn-testi-orb hn-testi-orb-2" />
        <div className="hn-inner">
          <div className="hn-testi-head">
            <div>
              <div className="hn-eyebrow-teal">Real stories</div>
              <h2 className="hn-h2">Sleep they&apos;d nearly given up on.</h2>
            </div>
          </div>

          <div className="hn-testi-layout">
            {TESTI.map((t, i) => (
              <figure className="hn-testi-card" key={t.n} data-testid={`testi-card-${i}`}>
                <div className="hn-testi-card-head">
                  <span className="hn-testi-card-num">{String(i + 1).padStart(2, "0")}</span>
                  <Mark color="#4FBEBE" size={20} />
                </div>
                <blockquote>
                  <span className="hn-testi-open">&ldquo;</span>{t.q}<span className="hn-testi-close">&rdquo;</span>
                </blockquote>
                <figcaption>
                  <div>
                    <strong>{t.n}</strong>
                    <span>{t.c}</span>
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="hn-faq" data-testid="faq-section">
        <div className="hn-faq-inner">
          <div className="hn-faq-header">
            <div className="hn-eyebrow-dark">Questions</div>
            <h2 className="hn-h2 dark">Everything you&apos;re wondering.</h2>
          </div>
          <div className="hn-faq-list">
            {FAQ.map((f, i) => <FaqRow key={f.q} idx={i} {...f} />)}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="hn-final" data-testid="final-cta">
        <div aria-hidden="true" className="hn-final-orb" />
        <div className="hn-final-inner">
          <div className="hn-final-mark"><Mark color="#4FBEBE" size={42} /></div>
          <h2 className="hn-final-h">Two quiet minutes tonight could change how tomorrow feels.</h2>
          <p>Take the free quiz and finally see your sleep, clearly.</p>
          <Link to="/quiz" className="hn-btn-teal large" data-testid="final-cta-btn">
            Take the free 2-minute quiz <span className="arr">&rarr;</span>
          </Link>
          <div className="hn-final-meta">Free &nbsp;·&nbsp; Private &nbsp;·&nbsp; No sign-up</div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
